// A review runs only when it is RELEVANT, like a human's (#6718, owner ruling 2026-10-20):
// it waits for the head's CI to settle, is skipped on red, and the box re-checks both at
// claim. This module is the vocabulary both ends of that check share — the hold window
// (`reviewCiHoldUntil`, the CI ceiling) and the two box-reported messages the mothership reads back. Pure; the SQL is
// `packages/db` `queries/tasks/ci-pending.ts`.
//
// The two messages are one writer/reader pair each (the `humanPushingMessage` /
// `parseHumanQuietUntil` shape, `../babysit/human-quiet`), so the box's sentence and the
// mothership's parse cannot drift: the box ends the run with the message, the failure write
// reads the instant or the reason back out of it.
//
// BOTH READERS ANCHOR ON THE WHOLE FIXED CLAUSE, not on a leading mark, which is where this
// pair departs from `parseHumanQuietUntil`: there the only interpolation before the mark is a
// GitHub login, here it is `detail`, a free-form line the box composes out of check names.
// `review-relevance.test.ts` pins both halves with a detail that quotes the other half back.

import { z } from 'zod';
import { CI_WAIT_CEILING_MS } from '../ci-wait';
import { TaskCancelReason } from './cancel-reason';

/**
 * Until when a review held {@link TaskHoldReason.CiPending} waits: the head's CI CEILING,
 * `headSeenAt + CI_WAIT_CEILING_MS`, or `now + CI_WAIT_CEILING_MS` when the head cannot be
 * dated (or is dated in the future — clock skew is no anchor). PURE.
 *
 * The WAKE is never this clock. A CI completion on the head re-grades it (the worker
 * releases or cancels), and the worker's server-side sweep is the fallback poll for heads
 * that send no completion event (a fork PR's checks, status-only CI). The window lapses
 * only where the box would review anyway — past the ceiling `reviewRelevance` answers
 * `timed_out` and the review runs, disclosed — so a box claims a held review only to
 * review it. A short window made the box the poller (prod 2026-10-22: one review claimed
 * 22 times in two hours on a 5-minute window, each claim only to re-read pending CI).
 *
 * A head already past the ceiling yields an instant in the past: claimable at once, and
 * reviewed `timed_out` at claim.
 */
export function reviewCiHoldUntil(headSeenAt: Date | null, now: Date): Date {
  const anchor = headSeenAt === null || headSeenAt > now ? now : headSeenAt;
  return new Date(anchor.getTime() + CI_WAIT_CEILING_MS);
}

// The instant rides a FIXED clause, and the reader anchors on the whole of it rather than on
// `ci recheck at ` alone, because `detail` is free-form box text: a check named
// `ci recheck at 2026-10-21T10:00:00Z` sat earlier in the same sentence and an `indexOf` on the
// short mark read THAT instant back as the hold window. The clause's three halves are shared
// with the writer below, so the pair cannot drift.
const CI_PENDING_LEAD = ', so the review waits — ci recheck at ';
const CI_PENDING_TAIL = '. Nothing failed and no attempt was charged.';
const CI_PENDING_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/;

/**
 * The message a box ends a review run with when it reads the head's CI still pending at
 * claim (failure kind `review_ci_pending`): it carries the instant to park until, which
 * {@link parseReviewCiPendingUntil} reads back so `failOrRetryTask` holds the row exactly
 * until then. `detail` is the box's own one-line reading (which checks are pending).
 */
export function reviewCiPendingMessage(until: Date, detail: string): string {
  return `the head's CI has not settled (${detail})${CI_PENDING_LEAD}${until.toISOString()}${CI_PENDING_TAIL}`;
}

/**
 * The instant a {@link reviewCiPendingMessage} names, or `null`. PURE. The LAST
 * `CI_PENDING_LEAD` in the message is the writer's — `detail` sits inside the
 * parenthetical before it — and the instant counts only when the writer's closing clause
 * follows it, so no text a box quotes can be read back as the hold window.
 */
export function parseReviewCiPendingUntil(message: string): Date | null {
  const at = message.lastIndexOf(CI_PENDING_LEAD);
  if (at < 0) return null;
  const rest = message.slice(at + CI_PENDING_LEAD.length);
  const iso = CI_PENDING_INSTANT.exec(rest)?.[0];
  if (iso === undefined || !rest.startsWith(CI_PENDING_TAIL, iso.length)) return null;
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? null : when;
}

/**
 * WHY a box found a claimed review no longer worth running — each member IS the
 * {@link TaskCancelReason} the mothership lands the row on (`cancelIrrelevantReview`), so
 * "why did this review not run" is answered off `cancel_reason` whichever end noticed.
 *
 *   `ci_red`     — the head's CI went red (revivable: a re-run turning it green revives it);
 *   `pr_settled` — the PR is closed or merged;
 *   `superseded` — the PR's head moved past the one this review was minted for;
 *   `pr_draft`   — the PR is a draft (`ready_for_review` mints a fresh review).
 */
export const ReviewIrrelevantReasonSchema = z.enum([
  TaskCancelReason.CiRed,
  TaskCancelReason.PrSettled,
  TaskCancelReason.Superseded,
  TaskCancelReason.PrDraft,
]);
export type ReviewIrrelevantReason = z.infer<typeof ReviewIrrelevantReasonSchema>;

const IRRELEVANT_MARK = 'review irrelevant: ';
// What ends the reason token. Shared with the writer, and REQUIRED by the reader: matching a
// bare `[a-z_]+` prefix accepted `ci_red2` as `ci_red`, inventing a reason the box never
// stated out of one this build happens to know the start of.
const IRRELEVANT_SEP = ' — ';

/**
 * The message a box ends a review run with when the review is no longer relevant at claim
 * (failure kind `review_irrelevant`). The reason leads, after a fixed mark, so
 * {@link parseReviewIrrelevantReason} reads it back; `detail` is the box's one-line reading.
 */
export function reviewIrrelevantMessage(reason: ReviewIrrelevantReason, detail: string): string {
  return `${IRRELEVANT_MARK}${reason}${IRRELEVANT_SEP}${detail}. The review was not run and nothing was charged.`;
}

/**
 * The reason a {@link reviewIrrelevantMessage} names, or `null` when the message carries
 * none this build knows — the caller then has no reason to cancel on, and must not invent one.
 */
export function parseReviewIrrelevantReason(message: string): ReviewIrrelevantReason | null {
  const at = message.indexOf(IRRELEVANT_MARK);
  if (at < 0) return null;
  const rest = message.slice(at + IRRELEVANT_MARK.length);
  const end = rest.indexOf(IRRELEVANT_SEP);
  if (end < 0) return null;
  const parsed = ReviewIrrelevantReasonSchema.safeParse(rest.slice(0, end));
  return parsed.success ? parsed.data : null;
}
